import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  BarChart, Button, Card, Tab, TabGroup, TabList,
  Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow,
  Text, Title,
} from '@tremor/react';
import type { CustomTooltipProps } from '@tremor/react';
import { fetchStoreTotals } from '../api/storeTotals';
import { currentUser } from '../api/auth';
import AccessDenied from '../components/AccessDenied';
import ErrorRetry from '../components/ErrorRetry';
import Header from '../components/Header';
import { localYMD } from '../lib/format';
import { t } from '../lib/i18n';
import { canUseReceiving } from '../lib/roles';
import { setPreferFullView } from '../lib/useIsMobile';
import type { StoreTotals, StoreTotalRow, User } from '../api/types';

// MXN formatter — 0 decimal places, matching the Caja KPI display style.
const fmtMxn = (n: number) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
  }).format(n);

// Format margin_pct (0-100 scale) as "XX.X%".
const fmtMargin = (n: number) => `${n.toFixed(1)}%`;

type WindowKey = 'today' | 'month' | 'week';
const WINDOWS: WindowKey[] = ['today', 'month', 'week'];

/** First day of the current month as YYYY-MM-DD. */
function firstOfMonth(): string {
  const d = new Date();
  return localYMD(new Date(d.getFullYear(), d.getMonth(), 1));
}

/** today minus N days as YYYY-MM-DD. */
function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

/** Map a WindowKey to {from, to} date strings. */
function windowDates(win: WindowKey): { from: string; to: string } {
  const today = localYMD();
  switch (win) {
    case 'today':  return { from: today, to: today };
    case 'month':  return { from: firstOfMonth(), to: today };
    case 'week':   return { from: daysAgo(6), to: today };
  }
}

// --------------------------------------------------------------------------
// Store-group rollup (the "Grupos" summary). Groups the per-store rows by the
// ERP st.GroupCode (`group_code`), summing `total` per group. Client-side —
// the daemon just tags each store row with its group_code.
export interface GroupTotal {
  label: string;
  total: number;
}

/**
 * Roll per-store rows up into store-group totals for the /movil "Grupos" cards.
 *  - Groups by trimmed `group_code`; blank codes collect into a "Sin grupo" bucket.
 *  - Returns [] when NO store has a group at all — the feature is absent and the
 *    section stays hidden (the "if available" fallback).
 *  - Real groups sort by total descending; "Sin grupo" always sorts last so the
 *    named regions lead. The returned totals sum to the company grand total.
 */
export function rollupGroups(stores: StoreTotalRow[]): GroupTotal[] {
  const byCode = new Map<string, number>();
  let anyGrouped = false;
  for (const s of stores) {
    const code = (s.group_code ?? '').trim();
    if (code !== '') anyGrouped = true;
    byCode.set(code, (byCode.get(code) ?? 0) + s.total);
  }
  if (!anyGrouped) return [];

  const out: GroupTotal[] = [];
  for (const [code, total] of byCode) {
    if (code === '') continue; // handled after the sort so it stays last
    out.push({ label: code, total });
  }
  out.sort((a, b) => b.total - a.total);

  const ungrouped = byCode.get('');
  if (ungrouped !== undefined) out.push({ label: t.movil.sinGrupo, total: ungrouped });
  return out;
}

// --------------------------------------------------------------------------
// Chart datum type: one row per store. The `Total` key is what Tremor plots;
// the underscore-prefixed fields ride along for the custom tooltip.
interface ChartDatum {
  tienda: string;      // store number string — the X-axis index
  Total: number;
  _group: string;      // trimmed group_code ('' = ungrouped) — for the Simple drill-down
  _name: string;       // full store name — shown in tooltip header
  _parts: number;
  _core: number;
  _labor: number;
  _freight: number;
  _tax: number;
  _other: number;
  _profit: number;
  _margin: number;
}

// --------------------------------------------------------------------------
// Custom tooltip: shows full store name + MXN breakdown on hover/tap.
// Mirrors the SalesDeltaTooltip factory pattern from SalesPeriodChart.

export function StoreBreakdownTooltip({ payload, active }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  // Recharts passes the full datum as payload[0].payload.
  const d = payload[0].payload as ChartDatum;

  const rows: Array<{ label: string; value: number }> = [
    { label: t.movil.seg.parts,   value: d._parts },
    { label: t.movil.seg.core,    value: d._core },
    { label: t.movil.seg.labor,   value: d._labor },
    { label: t.movil.seg.freight, value: d._freight },
    { label: t.movil.seg.tax,     value: d._tax },
    { label: t.movil.seg.other,   value: d._other },
  ].filter((r) => r.value !== 0);

  return (
    <div className="bg-tremor-background rounded-tremor-default shadow-lg border border-tremor-border p-2.5 text-xs min-w-[200px] max-w-[240px]">
      {/* Store name header */}
      <div className="font-semibold text-tremor-content-strong mb-2 leading-snug">
        {d._name}
      </div>

      {/* Breakdown rows */}
      <div className="space-y-0.5">
        {rows.map((r) => (
          <div key={r.label} className="flex justify-between gap-3 items-baseline">
            <span className="text-tremor-content">{r.label}</span>
            <span className="tabular-nums">{fmtMxn(r.value)}</span>
          </div>
        ))}
      </div>

      {/* Total */}
      <div className="mt-1.5 pt-1.5 border-t border-tremor-border flex justify-between gap-3 items-baseline">
        <span className="font-semibold text-tremor-content-strong">Total</span>
        <span className="tabular-nums font-bold text-tremor-content-strong">
          {fmtMxn(d.Total)}
        </span>
      </div>

      {/* Utilidad / Margen */}
      <div className="mt-1 flex justify-between gap-3 items-baseline">
        <span className="text-tremor-content">{t.movil.kpi.profit}</span>
        <span className="tabular-nums">{fmtMxn(d._profit)}</span>
      </div>
      <div className="flex justify-between gap-3 items-baseline">
        <span className="text-tremor-content">{t.movil.kpi.margin}</span>
        <span className="tabular-nums">{fmtMargin(d._margin)}</span>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------

export default function Movil() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  return user ? <MovilInner user={user} /> : <Navigate to="/login" replace />;
}

// --------------------------------------------------------------------------

function MovilInner({ user }: { user: User }) {
  const isOwner = user.role === 'owner';
  const navigate = useNavigate();

  // Default window: Hoy (today).
  const [win, setWin] = useState<WindowKey>('today');
  const [data, setData] = useState<StoreTotals | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { from, to } = windowDates(win);

  const load = () => {
    if (!isOwner) return;
    let alive = true;
    setErr(null);
    setLoading(true);
    fetchStoreTotals({ from, to })
      .then((r) => { if (alive) setData(r); })
      .catch((e: Error) => { if (alive) setErr(e.message ?? String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  };

  // Re-fetch whenever the window or auth changes.
  useEffect(() => { load(); }, [isOwner, from, to]);

  // Build chart data: one bar per store keyed by store number (sid).
  // Tremor plots only the `Total` category; breakdown fields ride in the datum
  // for the custom tooltip.
  const chartData: ChartDatum[] = useMemo(
    () => (data?.stores ?? []).map((s) => ({
      tienda:   String(s.sid),
      Total:    s.total,
      _group:   (s.group_code ?? '').trim(),
      _name:    s.store_name,
      _parts:   s.parts,
      _core:    s.core,
      _labor:   s.labor,
      _freight: s.freight,
      _tax:     s.tax,
      _other:   s.other,
      _profit:  s.profit,
      _margin:  s.margin_pct,
    })),
    [data],
  );

  // Store-group summary (empty → the Grupos section is hidden).
  const groups = useMemo(() => rollupGroups(data?.stores ?? []), [data]);

  // Simple (default) vs Detallado view. The toggle is only offered when the ERP
  // actually has groups; without groups Simple has nothing to drill, so we fall
  // back to the full (detailed) view. Remembered across visits.
  const [view, setView] = useState<'simple' | 'detailed'>(() => {
    try { return localStorage.getItem('pacebi-movil-view') === 'detailed' ? 'detailed' : 'simple'; }
    catch { return 'simple'; }
  });
  useEffect(() => { try { localStorage.setItem('pacebi-movil-view', view); } catch { /* ignore */ } }, [view]);

  // The group selected in Simple view. Defaults to the top group; re-anchors to
  // the top when the group set changes (e.g. a window switch) and the current
  // pick is no longer present.
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  useEffect(() => {
    if (groups.length === 0) { setSelectedGroup(null); return; }
    setSelectedGroup((cur) => (cur && groups.some((g) => g.label === cur) ? cur : groups[0].label));
  }, [groups]);

  // Chart data for the selected group's stores. "Sin grupo" (the ungrouped
  // bucket label) maps back to the blank-code stores.
  const groupChartData = useMemo(() => {
    if (!selectedGroup) return chartData;
    const wantBlank = selectedGroup === t.movil.sinGrupo;
    return chartData.filter((d) => (wantBlank ? d._group === '' : d._group === selectedGroup));
  }, [chartData, selectedGroup]);

  const showToggle = groups.length > 0;
  const simple = showToggle && view === 'simple';

  const totals = data?.totals;
  const winIdx = WINDOWS.indexOf(win);

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />

      <main className="flex-1 px-4 py-6 space-y-4 max-w-md mx-auto w-full">
        <div className="flex items-center justify-between gap-2">
          <Title>{t.movil.title}</Title>
          <div className="flex items-center gap-2">
            {/* Order Reception ("Recepción") entry point — visible to anyone
                with receiving:use (dock staff included), independent of the
                owner-only sales dashboard below. */}
            {canUseReceiving(user) && (
              <Link to="/po/scan" className="no-print">
                <Button variant="secondary" size="xs">{t.recepcion.navLabel}</Button>
              </Link>
            )}
            {/* no-print: escape hatch back to the full desktop dashboard */}
            {isOwner && (
              <Button
                className="no-print"
                variant="light"
                size="xs"
                onClick={() => { setPreferFullView(); navigate('/'); }}
              >
                {t.movil.fullView}
              </Button>
            )}
          </div>
        </div>

        {!isOwner ? (
          <AccessDenied user={user} requiredRoleLabel={t.roles.owner} />
        ) : (
          <>
            {/* Window toggle */}
            <TabGroup
              index={winIdx}
              onIndexChange={(i) => setWin(WINDOWS[i])}
            >
              <TabList>
                {WINDOWS.map((w) => (
                  <Tab key={w}>{t.movil.windows[w]}</Tab>
                ))}
              </TabList>
            </TabGroup>

            {err ? (
              <ErrorRetry message={err} onRetry={load} />
            ) : loading && !data ? (
              /* Simple loading skeleton — single-column so one placeholder card suffices. */
              <Card>
                <div className="h-10 bg-tremor-background-muted animate-pulse rounded" />
              </Card>
            ) : (
              <div className="space-y-4">
                {/* ── Simple / Detallado toggle (only when the ERP has groups) ── */}
                {showToggle && (
                  <TabGroup
                    index={view === 'simple' ? 0 : 1}
                    onIndexChange={(i) => setView(i === 0 ? 'simple' : 'detailed')}
                  >
                    <TabList variant="solid" className="w-full">
                      <Tab className="flex-1 justify-center">{t.movil.simple}</Tab>
                      <Tab className="flex-1 justify-center">{t.movil.detailed}</Tab>
                    </TabList>
                  </TabGroup>
                )}

                {/* ── Headline KPI ────────────────────────────────────────── */}
                <Card className="text-center">
                  <Text className="text-sm text-tremor-content">{t.movil.kpi.total}</Text>
                  {/* Fluid size (clamp) so even a 7-figure total never overflows
                      a narrow phone — caps at 3rem on wider screens. */}
                  <p className="mt-1 font-extrabold tracking-tight tabular-nums text-tremor-content-strong dark:text-dark-tremor-content-strong text-[clamp(1.75rem,8vw,3rem)]">
                    {fmtMxn(totals?.total ?? 0)}
                  </p>
                </Card>

                {simple ? (
                  /* ─── SIMPLE view (default): tap a group → see its stores ─── */
                  <>
                    {/* Tappable group tiles. The top group is selected on load
                        (see the selectedGroup effect); the highlighted tile drives
                        the chart below. 2 per row on a phone. */}
                    <Card>
                      <Title className="text-base">{t.movil.groups}</Title>
                      <Text className="mt-0.5 text-xs text-tremor-content">
                        {t.movil.groupHint}
                      </Text>
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        {groups.map((g) => {
                          const sel = g.label === selectedGroup;
                          return (
                            <button
                              type="button"
                              key={g.label}
                              onClick={() => setSelectedGroup(g.label)}
                              aria-pressed={sel}
                              className={
                                'text-left rounded-tremor-default border p-3 transition ' +
                                (sel
                                  ? 'border-tremor-brand ring-2 ring-tremor-brand bg-tremor-brand-faint'
                                  : 'border-tremor-border')
                              }
                            >
                              <Text className="text-xs text-tremor-content truncate" title={g.label}>
                                {g.label}
                              </Text>
                              <p className="mt-0.5 font-semibold tabular-nums text-tremor-content-strong dark:text-dark-tremor-content-strong text-[clamp(0.9rem,4.2vw,1.15rem)]">
                                {fmtMxn(g.total)}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </Card>

                    {/* The selected group's member stores, one bar each. */}
                    <Card>
                      <Title className="text-base">
                        {t.movil.groupStores}
                        {selectedGroup ? ` · ${selectedGroup}` : ''}
                      </Title>
                      <Text className="mt-0.5 text-xs text-tremor-content">
                        {t.movil.barHint}
                      </Text>
                      {groupChartData.length === 0 ? (
                        <Text className="mt-4">{t.movil.empty}</Text>
                      ) : (
                        <BarChart
                          className="mt-4 h-56"
                          data={groupChartData}
                          index="tienda"
                          categories={['Total']}
                          colors={['blue']}
                          valueFormatter={fmtMxn}
                          customTooltip={StoreBreakdownTooltip}
                          yAxisWidth={72}
                        />
                      )}
                    </Card>
                  </>
                ) : (
                <>
                {/* ── Secondary KPIs ──────────────────────────────────────── */}
                <div className="grid grid-cols-3 gap-2">
                  <Card className="p-3">
                    <Text className="text-xs text-tremor-content">{t.movil.kpi.profit}</Text>
                    <p className="mt-0.5 font-semibold tabular-nums text-tremor-content-strong dark:text-dark-tremor-content-strong truncate text-[clamp(0.8rem,3.6vw,1rem)]">
                      {fmtMxn(totals?.profit ?? 0)}
                    </p>
                  </Card>
                  <Card className="p-3">
                    <Text className="text-xs text-tremor-content">{t.movil.kpi.margin}</Text>
                    <p className="mt-0.5 font-semibold tabular-nums text-tremor-content-strong dark:text-dark-tremor-content-strong text-[clamp(0.8rem,3.6vw,1rem)]">
                      {fmtMargin(totals?.margin_pct ?? 0)}
                    </p>
                  </Card>
                  <Card className="p-3">
                    <Text className="text-xs text-tremor-content">{t.movil.kpi.tickets}</Text>
                    <p className="mt-0.5 font-semibold tabular-nums text-tremor-content-strong dark:text-dark-tremor-content-strong text-[clamp(0.8rem,3.6vw,1rem)]">
                      {totals?.tickets ?? 0}
                    </p>
                  </Card>
                </div>

                {/* ── Store-group summary ─────────────────────────────────── */}
                {/* Only rendered when the ERP has groups (st.GroupCode) defined;
                    otherwise this whole block is absent and the view is per-store
                    exactly as before. Total-only cards, 2 per row on a phone. */}
                {groups.length > 0 && (
                  <Card>
                    <Title className="text-base">{t.movil.groups}</Title>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {groups.map((g) => (
                        <div
                          key={g.label}
                          className="rounded-tremor-default border border-tremor-border p-3"
                        >
                          <Text className="text-xs text-tremor-content truncate" title={g.label}>
                            {g.label}
                          </Text>
                          <p className="mt-0.5 font-semibold tabular-nums text-tremor-content-strong dark:text-dark-tremor-content-strong text-[clamp(0.9rem,4.2vw,1.15rem)]">
                            {fmtMxn(g.total)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* ── Single-value bar chart ──────────────────────────────── */}
                {(data?.stores ?? []).length === 0 ? (
                  <Card>
                    <Text>{t.movil.empty}</Text>
                  </Card>
                ) : (
                  <>
                    <Card>
                      <Title className="text-base">{t.movil.byStore}</Title>
                      {/* Helper caption so the user knows to hover/tap for detail. */}
                      <Text className="mt-0.5 text-xs text-tremor-content">
                        {t.movil.barHint}
                      </Text>
                      {/* One bar per store. X-axis label = store number (sid) so even
                          chains with long store names stay legible on a narrow phone.
                          Breakdown detail is available in the custom tooltip on tap/hover. */}
                      <BarChart
                        className="mt-4 h-56"
                        data={chartData}
                        index="tienda"
                        categories={['Total']}
                        colors={['blue']}
                        valueFormatter={fmtMxn}
                        customTooltip={StoreBreakdownTooltip}
                        yAxisWidth={72}
                      />
                    </Card>

                    {/* ── Compact per-store table ─────────────────────────── */}
                    {/* Store number is the primary cell; full name available on
                        title hover (desktop) and visible in the chart tooltip.
                        Utilidad + Margen added as optional columns — they fit
                        cleanly on mobile when the store column is just a number. */}
                    <Card className="p-0 overflow-hidden">
                      {/* Horizontal scroll so all 5 columns stay reachable on a
                          narrow phone instead of the last one being clipped. */}
                      <div className="overflow-x-auto">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableHeaderCell className="w-10">#</TableHeaderCell>
                            <TableHeaderCell className="text-right">{t.movil.kpi.total}</TableHeaderCell>
                            <TableHeaderCell className="text-right">{t.movil.kpi.profit}</TableHeaderCell>
                            <TableHeaderCell className="text-right">{t.movil.kpi.margin}</TableHeaderCell>
                            <TableHeaderCell className="text-right">{t.movil.kpi.tickets}</TableHeaderCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(data?.stores ?? []).map((s) => (
                            <TableRow key={s.sid}>
                              {/* title carries the full name for desktop hover; the number
                                  keeps the column narrow and readable on mobile. */}
                              <TableCell
                                className="font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong"
                                title={s.store_name}
                              >
                                {s.sid}
                              </TableCell>
                              <TableCell className="text-right font-medium tabular-nums">
                                {fmtMxn(s.total)}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {fmtMxn(s.profit)}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {fmtMargin(s.margin_pct)}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {s.tickets}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </div>
                    </Card>
                  </>
                )}
                </>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
